import { isKnownUpgradeFeature, isKnownUpgradeOrigin, type UpgradeFeature } from './upgrade-origin';

/**
 * WHAT SENT A BUYER, carried past the picker to the purchase (docs/compliance/bi.md §2).
 *
 * The picker reads `origin` / `feature` off its own URL and strips them; the checkout creation, the
 * `buy_plan` click and — the point of all this — the verified purchase each need them again. The
 * purchase is the hard one: Stripe's return is a FRESH page load (hosted Checkout, or an Elements bank
 * redirect) that no longer carries the query, so the pair is stashed in sessionStorage when checkout
 * starts and read back when the payment verifies paid.
 *
 * Every read re-validates against the same allowlists `readUpgradeAttribution` uses — sessionStorage is
 * writable by anything on the origin, and a value that reaches BI unchecked is unbounded cardinality.
 * Every storage access is wrapped: private mode and blocked storage throw, and a purchase must never
 * fail because its attribution could not be kept.
 */
export interface PurchaseAttribution {
  origin?: string;
  feature?: UpgradeFeature;
}

const PURCHASE_ATTRIBUTION_KEY = 'symphony.purchaseAttribution';

/** Only the two validated fields, and nothing when neither is set. */
export function sanitizePurchaseAttribution(raw: unknown): PurchaseAttribution {
  if (!raw || typeof raw !== 'object') return {};
  const { origin, feature } = raw as { origin?: unknown; feature?: unknown };
  return {
    ...(isKnownUpgradeOrigin(origin) ? { origin } : {}),
    ...(isKnownUpgradeFeature(feature) ? { feature } : {}),
  };
}

/**
 * Written at checkout START. An unattributed checkout CLEARS the stash rather than leaving it: an
 * earlier attributed checkout in the same tab must not lend its origin to this one's purchase.
 */
export function stashPurchaseAttribution(attribution: PurchaseAttribution): void {
  const clean = sanitizePurchaseAttribution(attribution);
  try {
    if (!clean.origin && !clean.feature) {
      window.sessionStorage.removeItem(PURCHASE_ATTRIBUTION_KEY);
      return;
    }
    window.sessionStorage.setItem(PURCHASE_ATTRIBUTION_KEY, JSON.stringify(clean));
  } catch {
    /* sessionStorage unavailable — the purchase just reports unattributed */
  }
}

export function readPurchaseAttribution(): PurchaseAttribution {
  try {
    const raw = window.sessionStorage.getItem(PURCHASE_ATTRIBUTION_KEY);
    return raw ? sanitizePurchaseAttribution(JSON.parse(raw)) : {};
  } catch {
    return {};
  }
}

/** Cleared once the purchase it describes has been reported, so a later purchase cannot inherit it. */
export function clearPurchaseAttribution(): void {
  try {
    window.sessionStorage.removeItem(PURCHASE_ATTRIBUTION_KEY);
  } catch {
    /* nothing to clear */
  }
}

/**
 * The `purchase_verified` 198:10 `additionalFields`. `amount` is MAJOR units (`29.99`, `5000` for JPY) —
 * the same number `af_purchase` carries — as a decimal string, because additionalFields is string-valued.
 * Absent values are left undefined; the BI layer drops them rather than emitting blank keys.
 */
export function purchaseVerifiedFields(
  attribution: PurchaseAttribution,
  amount: number | null | undefined,
  currency: string | null | undefined,
): Record<string, string | undefined> {
  return {
    origin: attribution.origin,
    feature: attribution.feature,
    amount: typeof amount === 'number' && Number.isFinite(amount) ? String(amount) : undefined,
    currency: currency || undefined,
  };
}
